import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { KeyRound, ShieldAlert, Bug, Flag, Star } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchPosture, SP_STR as S, type SecurityPosture } from './securityApi'

function AreaCard({
  icon,
  title,
  to,
  attention,
  stats,
}: {
  icon: React.ReactNode
  title: string
  to?: string
  attention: boolean
  stats: { label: string; value: number; danger?: boolean }[]
}) {
  const inner = (
    <div className={`rounded-lg border p-4 ${attention ? 'border-danger/40 bg-danger/5' : 'border-border-default bg-surface-2'} ${to ? 'transition-colors hover:border-accent' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-tertiary">{icon}</span>
          <span className="text-sm font-medium text-primary">{title}</span>
        </div>
        <span className={`text-xs ${attention ? 'text-danger' : 'text-success'}`}>{attention ? S.attention : S.allClear}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-4">
        {stats.map((st) => (
          <div key={st.label}>
            <p className={`text-xl font-semibold tabular ${st.danger && st.value > 0 ? 'text-danger' : 'text-primary'}`}>{st.value}</p>
            <p className="text-xs text-tertiary">{st.label}</p>
          </div>
        ))}
      </div>
    </div>
  )
  return to ? <Link to={to}>{inner}</Link> : inner
}

export function SecurityPostureDashboard() {
  const [d, setD] = useState<SecurityPosture | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchPosture(ac.signal)
      .then(setD)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A18.10"
        description={S.desc}
        actions={
          <button type="button" onClick={() => setTick((t) => t + 1)} className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary">↻</button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl">
          {err ? (
            <ErrorState code={err.code} onRetry={() => setTick((t) => t + 1)} />
          ) : !d ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 5 }).map((_, i) => (<Skeleton key={i} className="h-28 w-full" />))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <AreaCard icon={<KeyRound size={16} />} title={S.jit} to="/jit/access" attention={d.jit.pending > 0} stats={[
                { label: S.pending, value: d.jit.pending, danger: true },
                { label: S.activeGrants, value: d.jit.active_grants },
              ]} />
              <AreaCard icon={<ShieldAlert size={16} />} title={S.breakGlass} to="/jit/break-glass" attention={d.break_glass.active > 0} stats={[
                { label: S.active, value: d.break_glass.active, danger: true },
                { label: S.total30d, value: d.break_glass.total_30d },
              ]} />
              <AreaCard icon={<Bug size={16} />} title={S.fraud} attention={d.fraud.open > 0} stats={[
                { label: S.open, value: d.fraud.open, danger: true },
                { label: S.total, value: d.fraud.total },
              ]} />
              <AreaCard icon={<Flag size={16} />} title={S.moderation} to="/automod/flags" attention={d.moderation.pending > 0} stats={[
                { label: S.pending, value: d.moderation.pending, danger: true },
                { label: S.total, value: d.moderation.total },
              ]} />
              <AreaCard icon={<Star size={16} />} title={S.points} attention={d.points_suspicions.open > 0} stats={[
                { label: S.open, value: d.points_suspicions.open, danger: true },
              ]} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
